import React, {useEffect} from "react";
import {Routes} from "./navigation/Routes";
import MediaTrackerApp from "./MediaTrackerApp";
import {
    authenticatedFetch,
    debouncedFetch,
    fetchPublishersBySearchTerm,
    getApolloClient,
    getPublishers,
    getUser,
    sortAndRemoveDuplicates
} from "./utils/services";
import AppHeader from "./components/AppHeader/app-header";
import {createBrowserHistory} from "history";
import Message from "./components/Message/message";
import {HomeButtons} from "./components/HomeButtons/home-button";
import {SideNavComponent, UserMenuData} from "./components/SideNav/side-nav";
import * as appConstant from "./constants/appConstant";
import {ApolloProvider} from "@apollo/client";
import MediaTrackerPageContent from "./components/MediaTrackerPageContent/media-tracker-page-content";
import FilterNav from "./components/FilterNav/FilterNav";
import CampaignsProvider from "./components/CampaignsContext/CampaignsProvider";
import * as util from "./utils/util";
import {deSanitizeObject} from "./utils/util";
import {getBearerToken, handleOAuthCallback, initAuth} from "@platform/platform-auth";
import {DateFilter} from "./components/DateFilter/DateFilter";
import PublisherSelectionDropdown from "./components/FilterNav/PublisherSelectionDropdown";
import {initializePendo} from "./components/Pendo/pendo";
import {ExcelDownload} from "./components/ExcelDownload/ExcelDownload";
import {OAuthCallback} from "./navigation/OAuthCallback";
import {ProtectedRoute} from "./navigation/ProtectedRoute";
import Spinner from "@cjdev-internal/visual-stack/lib/components/Spinner";
import {Box} from "@cjdev-internal/visual-stack";
import {Filters} from "./components/Filters/Filters";
import AccountSelectionDropdown from "./components/FilterNav/AccountSelectionDropdown";
import RemoveRecordModal from "./components/RemoveRecordModal/RemoveRecordModal";
import CampaignsDataTable from "./components/DataTable/CampaignsDataTable";
import {useModal} from "@cjdev-internal/visual-stack-x/Modal";
import {useAlert} from "@cjdev-internal/visual-stack-x/Alert";
import PlacementModalDialog from "./components/CreateRecord/PlacementModalDialog";
import PlacementDialogSubmitAction from "./components/CreateRecord/PlacementDialogSubmitAction";
import PlacementDialogSubmitEditAction from "./components/CreateRecord/PlacementDialogSubmitEditAction";
import PlacementDialogDuplicateSubmitAction from "./components/CreateRecord/PlacementDialogDuplicateSubmitAction";
import openModalDialogProvider from "./components/CreateRecord/OpenModalDialogProvider";
import AddPlacementButton from "./components/AddPlacementButton/AddPlacementButton";
import {combineReducers, createStore} from "redux";
import {reducer as visualStackReducer} from "@cjdev-internal/visual-stack-redux";
import {Provider} from "react-redux";
import SearchPanel from "./components/DataTable/SearchPanel";
import {ErrorPage} from "@cjdev-internal/visual-stack-x/ErrorPage";
import {DATATABLE_COLUMNS} from "./components/DataTable/DataTableConstant";

const CampaignsDefaultNavigation = () => {
    const history = createBrowserHistory();
    const authConfiguration = {
        "clientId": process.env.REACT_APP_CLIENT_ID,
        "redirectUri": process.env.REACT_APP_OAUTH_REDIRECT_URI,
    }
    const [authConfig, setAuthConfig] = React.useState(null);
    useEffect(async () => {
        initAuth(authConfiguration).then(() => {
            setAuthConfig(true);
        });
    }, [])

    const DefaultOAuthCallback = () => (
        <OAuthCallback handleOAuthCallback={handleOAuthCallback}/>
    );

    const client = getApolloClient();

    const defaultFetchPublishersBySearchTerm = fetchPublishersBySearchTerm(authenticatedFetch, sortAndRemoveDuplicates)

    const defaultGetPublishers = getPublishers(defaultFetchPublishersBySearchTerm)

    const DefaultPublisherSelectionDropdown = PublisherSelectionDropdown(defaultGetPublishers, debouncedFetch);
    const DefaultFilters = () => {
        return (<Filters AccountSelectionDropdown={AccountSelectionDropdown}
                         PublisherSelectionDropdown={DefaultPublisherSelectionDropdown}/>)
    }
    const DefaultFilterNav = () => (
        <FilterNav Filters={DefaultFilters}/>
    )

    const [modalMount, openModal, closeModal] = useModal();
    const [alertMount, openAlert, closeAlert] = useAlert();

    const modalConfiguration = {
        [appConstant.PLACEMENT_MODE_CREATE]: {
            ModalContent: PlacementModalDialog,
            props: {
                takeAction: PlacementDialogSubmitAction,
                placementModalTitle: appConstant.CREATE_RECORD_TITLE,
                modeOfPlacement: appConstant.PLACEMENT_MODE_CREATE,
            }
        },
        [appConstant.PLACEMENT_MODE_EDIT]: {
            ModalContent: PlacementModalDialog,
            props: {
                takeAction: PlacementDialogSubmitEditAction,
                placementModalTitle: appConstant.EDIT_RECORD_TITLE,
                modeOfPlacement: appConstant.PLACEMENT_MODE_EDIT,
            }
        },
        [appConstant.PLACEMENT_MODE_DUPLICATE]: {
            ModalContent: PlacementModalDialog,
            props: {
                takeAction: PlacementDialogDuplicateSubmitAction,
                placementModalTitle: appConstant.DUPLICATE_RECORD_TITLE,
                modeOfPlacement: appConstant.PLACEMENT_MODE_DUPLICATE,
            }
        },
        [appConstant.PLACEMENT_MODE_REMOVE]: {
            ModalContent: RemoveRecordModal
        }
    }
    const openModalDialog = openModalDialogProvider(openModal, closeModal, openAlert, closeAlert, modalConfiguration, getBearerToken, deSanitizeObject);

    const DefaultCampaignsDataTable = CampaignsDataTable(DATATABLE_COLUMNS, openModalDialog, SearchPanel)

    const DefaultExcelDownload = () => <ExcelDownload util={util}/>;


    const AddPlacementButtonWrapper = () => <AddPlacementButton openModalDialog={openModalDialog}/>

    const WelcomePageButton = () => {
        return (<HomeButtons CreateRecordModal={AddPlacementButtonWrapper} getBearerToken={util.getBearerToken}/>);
    }

    const DefaultAppHeader = AppHeader(AddPlacementButtonWrapper, DateFilter, DefaultExcelDownload);

    const DefaultMessage = Message(WelcomePageButton);

    const DefaultMediaTrackerPageContent = MediaTrackerPageContent(DefaultCampaignsDataTable, DefaultMessage);


    const DefaultMediaTrackerApp = () => (
        <DefaultCampaignsProvider>
            {alertMount}
            {modalMount}
            <MediaTrackerApp
                getUser={getUser}
                AppHeader={DefaultAppHeader}
                UserMenuData={UserMenuData}
                SideNavComponent={SideNavComponent}
                INDEX_PAGE={appConstant.INDEX_PAGE}
                MediaTrackerPageContent={DefaultMediaTrackerPageContent}
                Message={Message}
                FilterNav={DefaultFilterNav}
                getCompaniesDetails={util.getCompaniesDetails}
                getNonEmptyVariables={util.getNonEmptyVariables}
                initializePendo={initializePendo}
            />
        </DefaultCampaignsProvider>
    );

    const LoadingAnimation = () => {
        return <Box direction="row" justify="center">
            <Spinner/>
        </Box>
    }

    const CampaignsMaintenance = () => (
        <ErrorPage autoPosition={true}
                   errorCode={999}/>
    )
    const DefaultProtectedRoute = () => {
        return <ProtectedRoute Component={DefaultMediaTrackerApp} LoadingComponent={LoadingAnimation}
                               getBearerToken={getBearerToken} CampaignsMaintenance={CampaignsMaintenance}/>
    }

    const reducer = combineReducers({
        visualStack: visualStackReducer,
    });
    const store = createStore(reducer);
    const DefaultCampaignsProvider = ({children}) => {
        return <Provider store={store}>
            <ApolloProvider client={client}>
                <CampaignsProvider getUserLinkedAdvertiser={util.getUserLinkedAdvertiser}>
                    {children}
                </CampaignsProvider>
            </ApolloProvider>
        </Provider>
    }

    return authConfig ? <Routes
        history={history}
        ProtectedRoute={DefaultProtectedRoute}
        OAuthCallback={DefaultOAuthCallback}
    /> : ""
}

export default CampaignsDefaultNavigation;
